/**
 * Admin 공지 CRUD. 라우트 admin notice 그룹과 동기화.
 * create/edit: Quill 에디터, 콘텐츠 유형 탭, 폼 제출 시 body 동기화.
 */
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";
import { QuillEditor, stripBase64Images } from "../../components/QuillEditor";

function noop(action: string): void {
  Logger.debug(`AdminNoticeController.${action} (no-op)`);
}

function inferContentMode(html: string): "text" | "image" | "link" {
  const stripped = (html || "").replace(/<[^>]+>/g, "").trim();
  if (!stripped) return "image";
  const linkMatch = (html || "").match(
    /^<p>\s*<a\s+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>\s*<\/p>$/i
  );
  if (linkMatch) return "link";
  return "text";
}

function parseLinkFromBody(html: string): { url: string; title: string } {
  const m = (html || "").match(/<a\s+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/i);
  return m ? { url: m[1], title: m[2] ?? "" } : { url: "", title: "" };
}

function initNoticeForm(): void {
  const form = document.getElementById("notice-form") as HTMLFormElement | null;
  const editorEl = document.getElementById("notice-body-editor");
  const editorEnEl = document.getElementById("notice-body-en-editor");
  const bodyInput = document.getElementById("notice-body-input") as HTMLInputElement | null;
  const bodyEnInput = document.getElementById("notice-body-en-input") as HTMLInputElement | null;
  const contentModeInput = document.getElementById("content-mode-input") as HTMLInputElement | null;
  const tabBtns = document.querySelectorAll(".admin-notice-tabs__tab");
  const panelText = document.getElementById("notice-panel-text");
  const panelImage = document.getElementById("notice-panel-image");
  const panelLink = document.getElementById("notice-panel-link");
  const linkUrlEl = document.getElementById("notice-link-url") as HTMLInputElement | null;
  const linkTitleEl = document.getElementById("notice-link-title") as HTMLInputElement | null;

  if (!form || !bodyInput || !contentModeInput) return;

  const uploadImageUrl = form.dataset.uploadImageUrl ?? "";
  const csrf = (document.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ?? "";

  let initialBody = "";
  const initEl = document.getElementById("notice-body-initial");
  if (initEl?.textContent) {
    try {
      initialBody = JSON.parse(initEl.textContent) as string;
    } catch {
      initialBody = "";
    }
  }
  let initialBodyEn = "";
  const initEnEl = document.getElementById("notice-body-en-initial");
  if (initEnEl?.textContent) {
    try {
      initialBodyEn = JSON.parse(initEnEl.textContent) as string;
    } catch {
      initialBodyEn = "";
    }
  }

  const initialMode = inferContentMode(initialBody);
  contentModeInput.value = initialMode;

  const uploadConfig =
    uploadImageUrl && csrf
      ? {
          uploadUrl: uploadImageUrl,
          csrf,
          fileKey: "image" as const,
          getUrl: (json: Record<string, unknown>) =>
            (json?.data as Record<string, unknown>)?.url as string | undefined,
        }
      : undefined;

  let quillEditor: QuillEditor | null = null;
  if (editorEl) {
    quillEditor = new QuillEditor(editorEl, {
      placeholder: "내용을 입력하세요. 이미지는 툴바 이미지 버튼으로 넣을 수 있습니다.",
      initialHtml: initialMode === "text" ? initialBody : "",
      upload: uploadConfig,
    });
    quillEditor.init();
  }

  let quillEditorEn: QuillEditor | null = null;
  if (editorEnEl) {
    quillEditorEn = new QuillEditor(editorEnEl, {
      placeholder: "Content (EN). You can add images via the toolbar.",
      initialHtml: initialBodyEn ?? "",
      upload: uploadConfig,
    });
    quillEditorEn.init();
  }

  const showPanel = (mode: string): void => {
    contentModeInput.value = mode;
    [panelText, panelImage, panelLink].forEach((p) => {
      if (p) p.classList.add("admin-notice-panel--hidden");
    });
    tabBtns.forEach((btn) => {
      btn.classList.remove("admin-notice-tabs__tab--active");
      btn.setAttribute("aria-selected", "false");
    });
    const panel =
      mode === "text" ? panelText : mode === "image" ? panelImage : panelLink;
    const tab = document.querySelector(`.admin-notice-tabs__tab[data-tab="${mode}"]`);
    if (panel) panel.classList.remove("admin-notice-panel--hidden");
    if (tab) {
      tab.classList.add("admin-notice-tabs__tab--active");
      tab.setAttribute("aria-selected", "true");
    }
  };

  tabBtns.forEach((btn) => {
    btn.addEventListener("click", () => showPanel(btn.getAttribute("data-tab") ?? "text"));
  });
  showPanel(initialMode);

  if (initialMode === "link") {
    const linkInfo = parseLinkFromBody(initialBody);
    if (linkUrlEl) linkUrlEl.value = linkInfo.url;
    if (linkTitleEl) linkTitleEl.value = linkInfo.title;
  }

  form.addEventListener(
    "submit",
    (e) => {
      const mode = contentModeInput.value;
      if (mode === "text") {
        bodyInput.value = stripBase64Images(
          quillEditor?.getHtml() ?? ""
        );
        if (bodyEnInput) {
          bodyEnInput.value = stripBase64Images(
            quillEditorEn?.getHtml() ?? ""
          );
        }
      } else if (mode === "image") {
        bodyInput.value = "";
        if (bodyEnInput) bodyEnInput.value = "";
      } else if (mode === "link") {
        const url = linkUrlEl?.value?.trim() ?? "";
        const title = (linkTitleEl?.value?.trim() ?? url) || url;
        if (!url) {
          void Modal.alert("링크 URL을 입력하세요.");
          e.preventDefault();
          return;
        }
        const safeTitle = (title || url).replace(/</g, "&lt;").replace(/>/g, "&gt;");
        bodyInput.value = '<p><a href="' + url.replace(/"/g, "&quot;") + '">' + safeTitle + "</a></p>";
        if (bodyEnInput) bodyEnInput.value = "";
      }
    },
    true
  );
}

export const AdminNoticeController = {
  index(): void {
    noop("index");
  },
  create(): void {
    initNoticeForm();
  },
  store(): void {
    noop("store");
  },
  edit(): void {
    initNoticeForm();
  },
  update(): void {
    noop("update");
  },
  destroy(): void {
    noop("destroy");
  },
};
